<template>
  <div :class="classes">
    <b-tag
      v-for="tag in modelValue"
      :key="tag"
      closable
      :disable-transitions="false"
      @close="handleClear(tag)"
      >{{ tag }}</b-tag
    >
    <div
      :contenteditable="isFocus"
      :tabindex="tabIndex"
      :class="[ns.e('textbox'), ns.is('focus', isFocus)]"
      @keydown="handleKeydown"
      @keyup="handleKeyup"
      @keydown.enter.prevent
      @blur="handleBlur"
      @focus="handleFocus"
    >
      {{ modelValue.length === 0 && !isFocus ? placeholder : '' }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import BTag from '@bigin/components/tag'
import { useFormItem, useNamespace } from '@bigin/hooks'
import { tagInputEmits, tagInputProps } from './tag-input'

defineOptions({
  name: 'BTagInput',
})
const props = defineProps(tagInputProps)
const emit = defineEmits(tagInputEmits)

const { formItem } = useFormItem()
const ns = useNamespace('tag-input')

const isError = computed(() => Boolean(formItem?.error))
const isFocus = ref(false)

const classes = computed(() => {
  return [
    ns.b(),
    ns.is('rounded', props.rounded),
    ns.is('focus', isFocus.value),
    ns.is('error', isError.value),
  ]
})

// methods
const handleFocus = (event: Event) => {
  const elm = event.target as HTMLDivElement
  elm.innerText = ''
  isFocus.value = true
}

const handleClear = (tag: string) => {
  emit(
    'update:modelValue',
    props.modelValue.filter((item) => item !== tag)
  )
  emit(
    'changed',
    props.modelValue.filter((item) => item !== tag)
  )
}

const handleCommit = (elm: HTMLDivElement) => {
  const _value = elm.innerText
  const isValid =
    _value.length &&
    props.validator(_value) &&
    !props.modelValue.includes(_value)

  if (isValid) {
    emit('update:modelValue', [...props.modelValue, _value])
    emit('changed', [...props.modelValue, _value])
    emit('error', 'none')
    elm.innerText = ''
  } else if (_value.length === 0) {
    emit('error', 'required')
  } else if (!props.validator(_value)) {
    emit('error', 'validation')
  } else if (props.modelValue.includes(_value)) {
    emit('error', 'duplicate')
  }
}

const handleKeydown = (e: KeyboardEvent) => {
  const elm = e.target as HTMLDivElement
  const keyName = e.key

  if (keyName === props.commitKey) {
    e.preventDefault()
    handleCommit(elm)
  }
}

const handleKeyup = (e: KeyboardEvent) => {
  const keyName = e.key

  if (
    ['Backspace', 'Delete'].includes(keyName) &&
    (e.target as HTMLDivElement).innerText.length === 0
  ) {
    emit('update:modelValue', props.modelValue.slice(0, -1))
    emit('changed', props.modelValue.slice(0, -1))
  }
}

const handleBlur = (e: Event) => {
  const elm = e.target as HTMLDivElement
  isFocus.value = false
  handleCommit(e.target as HTMLDivElement)

  if (elm.innerText === '' && props.modelValue.length === 0) {
    elm.innerText = props.placeholder
  }
}
</script>
